if (!customElements.get("m-announcement-bar")) {
  class MAnnouncementBar extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.querySelectorAll("[data-klaviyo-open]").forEach((button) => {
        button.addEventListener("click", (event) => this.openKlaviyo(button, event));
      });
      this.querySelectorAll("[data-discount-apply]").forEach((button) => {
        button.addEventListener("click", () => this.applyDiscount(button));
      });
      this.onCartUpdated = () => this.syncAppliedCodes();
      document.addEventListener("cart:updated", this.onCartUpdated);
      this.syncAppliedCodes();
      this.bindStick();

      const enableSlider = this.dataset.enableSlider === "true";
      if (!enableSlider) return;
      this.initSlider();
    }

    // Two reveal modes, set by sections/annoucement.liquid via data-reveal on the section:
    //  - "viewport" (checkout landings): the bar is fixed and hidden until the visitor scrolls past the
    //    first viewport, then slides in. It slides back out when they return to the hero.
    //  - "quarter" (every other page): the bar sits in the flow at the top, scrolls away, and re-pins once
    //    the visitor is 25% down the page. It stays pinned until they are back at the very top.
    bindStick() {
      this.section = this.closest(".m-announcement-bar");
      if (!this.section) return;
      this.revealMode = this.section.dataset.reveal === "viewport" ? "viewport" : "quarter";
      this.passedThreshold = false;
      this.lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      if (this.revealMode === "quarter") {
        this.placeholder = document.createElement("div");
        this.placeholder.className = "m-announcement-bar__placeholder";
        this.placeholder.setAttribute("aria-hidden", "true");
        this.placeholder.hidden = true;
        this.section.after(this.placeholder);
      }
      this.onStick = () => this.updateStick();
      window.addEventListener("scroll", this.onStick, { passive: true });
      window.addEventListener("resize", this.onStick);
      this.updateStick();
    }

    updateStick() {
      if (!this.section) return;
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const wasStuck = this.section.classList.contains("is-stuck");
      const scrollingDown = scrollY > this.lastScrollY + 1;
      this.lastScrollY = scrollY;
      let stuck;

      if (this.revealMode === "viewport") {
        // Past the first viewport: show. Back inside it: hide.
        stuck = scrollY >= window.innerHeight;
      } else {
        const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const quarter = scrollable * 0.25;
        const atTop = scrollY <= 2;
        if (atTop) this.passedThreshold = false;
        else if (scrollable > 0 && scrollY >= quarter) this.passedThreshold = true;
        stuck = this.passedThreshold && !atTop;
      }

      this.section.classList.toggle("is-stuck", stuck);
      document.documentElement.classList.toggle("announcement-is-stuck", stuck);

      if (this.revealMode === "quarter") {
        if (!stuck) {
          this.section.classList.remove("is-pinning");
        } else if (!wasStuck && scrollingDown && !reducedMotion) {
          this.section.classList.remove("is-pinning");
          void this.section.offsetWidth;
          this.section.classList.add("is-pinning");
        }
      }

      if (stuck) {
        const height = this.section.offsetHeight;
        if (this.placeholder) {
          this.placeholder.hidden = false;
          this.placeholder.style.height = `${height}px`;
        }
        document.documentElement.style.setProperty("--m-announcement-height", `${height}px`);
      } else {
        if (this.placeholder) this.placeholder.hidden = true;
        // Landings have no header script to own this variable, so keep it honest while the bar is hidden.
        if (this.revealMode === "viewport") document.documentElement.style.setProperty("--m-announcement-height", "0px");
      }
    }

    openKlaviyo(button, event) {
      event.preventDefault();
      const mobile = window.matchMedia("(max-width: 767px)").matches;
      const formId = mobile ? button.dataset.klaviyoFormMobile : button.dataset.klaviyoFormDesktop;
      if (!formId) return;
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(["openForm", formId]);
    }

    discountCode(button) {
      const codeEl = button.querySelector(".discount-code");
      return (codeEl ? codeEl.textContent : "").replace(/\s+/g, "").toUpperCase();
    }

    statusEl(button) {
      return button.parentElement.querySelector("[data-discount-status]");
    }

    setStatus(button, message) {
      const status = this.statusEl(button);
      if (!status) return;
      status.hidden = !message;
      status.textContent = message || "";
    }

    existingCodes() {
      return [...document.querySelectorAll("[data-minimog-cart-discounts] .m-cart__discount--item")]
        .map((item) => (item.dataset.discountCode || "").toUpperCase())
        .filter(Boolean);
    }

    async syncAppliedCodes() {
      if (!window.MinimogSettings) return;
      try {
        const cart = await (await fetch(`${MinimogSettings.routes.cart}.js`, { headers: { Accept: "application/json" } })).json();
        const applied = new Set((cart.discount_codes || []).filter((discount) => discount.applicable || cart.item_count === 0).map((discount) => discount.code.toUpperCase()));
        this.querySelectorAll("[data-discount-apply]").forEach((button) => {
          const code = this.discountCode(button);
          const isApplied = Boolean(code) && applied.has(code);
          button.classList.toggle("is-applied", isApplied);
          button.setAttribute("aria-pressed", isApplied ? "true" : "false");
        });
      } catch (error) {
        console.error(error);
      }
    }

    async applyDiscount(button) {
      const code = this.discountCode(button);
      if (!code || button.classList.contains("is-busy") || !window.MinimogSettings) return;

      if (button.classList.contains("is-applied")) {
        const drawer = document.querySelector("m-cart-drawer");
        if (drawer) drawer.open(button);
        return;
      }

      button.classList.add("is-busy");
      button.setAttribute("aria-busy", "true");
      this.setStatus(button, "");

      const codes = [...new Set([...this.existingCodes(), code])];

      try {
        const state = await this.updateDiscounts(codes);
        const match = (state.discount_codes || []).find((discount) => discount.code.toUpperCase() === code);
        const cart = await (await fetch(`${MinimogSettings.routes.cart}.js`, { headers: { Accept: "application/json" } })).json();

        if (!match || (match.applicable === false && cart.item_count > 0)) {
          const remaining = codes.filter((existing) => existing !== code);
          await this.updateDiscounts(remaining);
          button.classList.remove("is-applied");
          button.setAttribute("aria-pressed", "false");
          const hasPlan = (cart.items || []).some((item) => item.selling_plan_allocation);
          this.setStatus(
            button,
            hasPlan ? `${code} doesn't apply to Subscribe & Save.` : `${code} doesn't apply to this order.`
          );
          return;
        }

        button.classList.add("is-applied");
        button.setAttribute("aria-pressed", "true");

        if (cart.item_count > 0) {
          this.setStatus(button, "");
          const drawer = document.querySelector("m-cart-drawer");
          if (drawer) drawer.open(button);
        } else {
          this.setStatus(button, `${code} is saved. It will show in your cart.`);
        }
      } catch (error) {
        console.error(error);
        this.setStatus(button, `${code} could not be applied.`);
      } finally {
        button.classList.remove("is-busy");
        button.removeAttribute("aria-busy");
      }
    }

    async updateDiscounts(codes) {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } })
      );

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData();
      formData.append("sections", sections);
      formData.append("sections_url", window.location.pathname);
      formData.append("discount", codes.join(","));
      config.body = formData;

      const parsedState = await (await fetch(MinimogSettings.routes.cart_update_url, config)).json();
      const cartJson = await (await fetch(`${MinimogSettings.routes.cart}.js`, { headers: { Accept: "application/json" } })).json();
      cartJson.sections = parsedState.sections;

      if (window.MinimogEvents && window.MinimogTheme) {
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: cartJson });
      }

      return parsedState;
    }

    disconnectedCallback() {
      if (this.onCartUpdated) document.removeEventListener("cart:updated", this.onCartUpdated);
      if (this.onStick) {
        window.removeEventListener("scroll", this.onStick);
        window.removeEventListener("resize", this.onStick);
      }
      if (this.placeholder) this.placeholder.remove();
      document.documentElement.classList.remove("announcement-is-stuck");
    }

    initSlider() {
      const autoplay = this.dataset.enableAutoplay === "true";
      const autoplaySpeed = this.dataset.autoplaySpeed;
      const slideContainer = this.querySelector(".swiper-container");
      if (slideContainer) {
        this.slider = new MinimogLibs.Swiper(slideContainer, {
          slidesPerView: 1,
          slidesPerGroup: 1,
          autoplay: autoplay
            ? {
                delay: parseInt(autoplaySpeed) * 1000,
              }
            : false,
          loop: true,
          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },
        });
      }
    }
  }

  customElements.define("m-announcement-bar", MAnnouncementBar);
}
